import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ElementRef,
  NgModule,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { FieldDataUpsertOperationInterface } from '@vendasta/account-group';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import AutoNumeric from 'autonumeric';
import { FieldHandler, FieldHandlerValidator } from '../auxiliary-data-table/auxiliary-data-table.interface';
import { calculateCurrencyDisplay, maxValue, minValue } from '../currency';
import { ReadOnlyStyles } from './common';

export function galaxyToApi(hasDecimals: number): number {
  if (hasDecimals > 0) {
    return Math.floor(hasDecimals * 100);
  }
  return Math.ceil(hasDecimals * 100);
}

export function apiToGalaxy(noDecimalsJustPennies: number): number {
  return noDecimalsJustPennies / 100;
}

export class CurrencyFieldHandler implements FieldHandler {
  description: string;
  formControl: UntypedFormControl;

  constructor(
    public fieldId: string,
    public name: string,
    public label: string,
    public value: string,
    private currencyCode: string,
    public validators: FieldHandlerValidator[],
    public readOnly = false,
  ) {
    this.formControl = new UntypedFormControl(parseInt(value, 10));
    this.formControl.setValidators(validators.map((v) => v.validatorFn));
  }

  render(viewContainerRef: ViewContainerRef): void {
    viewContainerRef.clear();
    const component = viewContainerRef.createComponent<CurrencyRendererComponent>(CurrencyRendererComponent);
    component.instance.control = this.formControl;
    component.instance.label = this.label;
    component.instance.currencyCode = this.currencyCode;
    component.instance.validators = this.validators;
    component.instance.readOnly = this.readOnly;
  }

  hasModification(): boolean {
    return this.formControl?.dirty;
  }

  upsertOperation(): FieldDataUpsertOperationInterface {
    if (this.formControl.value === null) {
      return {
        fieldDataId: this.fieldId,
        deleteValue: true,
      };
    }

    return {
      fieldDataId: this.fieldId,
      currencyValue: this.formControl.value,
    };
  }
}

@Component({
  styles: [
    `
      :host,
      mat-form-field,
      input {
        width: 100%;
      }
      #currency {
        margin: 0 5px;
        padding: 6px 0 8px;
      }
      #currency ::ng-deep .input-and-hints {
        margin: 0;
      }
    `,
    ...ReadOnlyStyles,
  ],
  template: `
    <div *ngIf="readOnly; else formField">
      <div class="label">
        {{ label }}
      </div>
      <div class="value">
        {{ readOnlyDisplay }}
      </div>
    </div>
    <ng-template #formField>
      <glxy-form-field #formField id="currency" [size]="'large'" [prefixText]="currencyCode">
        <input
          [placeholder]="'COMMON.ADD_FIELD' | translate: { fieldName: label?.toLowerCase() }"
          matInput
          (keydown)="key($event)"
          (keyup)="key($event)"
          #input
        />
        <ng-container *ngFor="let validator of validators" ngProjectAs="glxy-error">
          <glxy-error *ngIf="control.hasError(validator.errorCode)">
            {{ validator.errorMessage | translate }}
          </glxy-error>
        </ng-container>
      </glxy-form-field>
    </ng-template>
  `,
  standalone: false,
})
export class CurrencyRendererComponent implements AfterViewInit, AfterContentInit {
  @ViewChild('input') input: ElementRef;

  maxValue = maxValue;
  minValue = minValue;

  control!: UntypedFormControl;
  label!: string;
  currencyCode!: string;
  validators!: FieldHandlerValidator[];
  readOnly!: boolean;

  readOnlyDisplay: string;

  ngAfterViewInit(): void {
    const num: number = this.control.value;
    if ((num || num === 0) && !this.readOnly) {
      this.setInput(num);
    }
  }

  ngAfterContentInit(): void {
    const num: number = this.control.value;
    if (this.readOnly) {
      this.readOnlyDisplay = this.getReadOnlyDisplayName(num);
    }
  }

  key(event: any): void {
    const num: number = calculateCurrencyDisplay(event);
    if (num) {
      this.input.nativeElement.value = apiToGalaxy(num).toFixed(2);
    } else {
      this.input.nativeElement.value = null;
    }
    this.control.setValue(num);
    this.control.markAsDirty();
  }

  clear(): void {
    this.control.setValue(null);
    this.control.markAsDirty();
    this.input.nativeElement.value = null;
  }

  private setInput(num: number): void {
    this.input.nativeElement.value = apiToGalaxy(num).toFixed(2);
  }

  private getReadOnlyDisplayName(num: number): string {
    if (!num || Number.isNaN(num)) {
      return '-';
    }

    const displayValue = (num / 100).toFixed(2);
    const formattedPrice = AutoNumeric.format(displayValue, {
      maximumValue: maxValue.toString(),
      minimumValue: minValue.toString(),
    });
    return `${this.currencyCode} ${formattedPrice}`;
  }
}

@NgModule({
  declarations: [CurrencyRendererComponent],
  exports: [CurrencyRendererComponent],
  imports: [CommonModule, GalaxyFormFieldModule, MatInputModule, ReactiveFormsModule, TranslateModule],
})
export class CurrencyRendererComponentModule {}
